import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { KINDS, PLACES, CITIES, Kind } from '../data/smaken';
import { PlaceCard } from '../components/PlaceCard';
import { Producers } from '../components/home/Producers';
import { EventsSection } from '../components/home/EventsSection';
import { Reveal } from '../components/Reveal';
import { Note } from '../components/Marks';
const AREAS = Object.keys(CITIES);
export function Discover() {
  const [params, setParams] = useSearchParams();
  const kind = params.get('kategori') as Kind | null;
  const query = params.get('q') ?? '';
  const area = params.get('ort') ?? '';
  const onlyNew = params.get('filter') === 'nytt';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PLACES.filter(place => {
      if (kind && place.kind !== kind) return false;
      if (area && place.city !== area) return false;
      if (onlyNew && !place.isNew) return false;
      if (!q) return true;
      const haystack = [place.name, place.city, place.blurb, ...place.tags].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [kind, query, area, onlyNew]);
  const activeKind = KINDS.find(k => k.id === kind);
  const heading = onlyNew ? 'NYTT' : activeKind ? activeKind.label : 'UPPTÄCK';
  const sub = onlyNew ? 'Nya platser att upptäcka' : activeKind ? activeKind.sub : 'Allt vi kan stå för i Småland.';
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-868" data-mp="21kqwij56mc59">
      <Reveal data-id="element-869" data-mp="4roxxuuavwmo">
        <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-870" data-mp="4s8i7i7fax3v">{heading}</h1>
        <p className="mt-3 text-lg text-black/60" data-id="element-871" data-mp="3vy06ec1n3zo1">{sub}</p>
      </Reveal>

      {/* Controls */}
      <Reveal delay={0.05} data-id="element-872" data-mp="ix8ufcxnjdsr">
        <div className="mt-10 border-t-2 border-black pt-5" data-id="element-873" data-mp="iwz23ip4wujw">
          <div className="flex items-center gap-3 border-b border-black/15 pb-4" data-id="element-874" data-mp="ive7jt2tlbm1">
            <SearchIcon className="h-5 w-5 shrink-0 text-black/40" aria-hidden="true" data-id="element-875" data-mp="nq1zk1x18o5d" />
            <label htmlFor="discover-q" className="sr-only" data-id="element-876" data-mp="9y3uc26fzceh">
              Sök
            </label>
            <input id="discover-q" value={query} onChange={e => setParam('q', e.target.value)} placeholder="Bageri, lunch, gårdsbutik, kaffe…" className="w-full bg-transparent font-display text-xl outline-none placeholder:text-black/30 sm:text-2xl" data-id="element-877" data-mp="ew5i30etbzwa" />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-878" data-mp="xllt4ho4jaqy">
            <button onClick={() => setParams(new URLSearchParams(query ? {
            q: query
          } : {}), {
            replace: true
          })} aria-pressed={!kind && !onlyNew} className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${!kind && !onlyNew ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`} data-id="element-879" data-mp="xny8qo6et90u">
              Alla
            </button>
            {KINDS.map(k => <button key={k.id} onClick={() => setParam('kategori', kind === k.id ? '' : k.id)} aria-pressed={kind === k.id} className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${kind === k.id ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`} data-id="element-880" data-mp="3evq5dhx4ppes">
                {k.label}
              </button>)}

            <div className="relative ml-auto" data-id="element-881" data-mp="28cjjngfzy6lk">
              <label htmlFor="discover-area" className="sr-only" data-id="element-882" data-mp="23fjfxc6mdfxm">
                Ort
              </label>
              <select id="discover-area" value={area} onChange={e => setParam('ort', e.target.value)} className="cursor-pointer border border-black/20 bg-transparent px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 outline-none transition-colors duration-150 ease-out hover:border-black" data-id="element-883" data-mp="2h2iboa3it8o2">
                <option value="" data-id="element-884" data-mp="2zshwgksbw1lo">Hela Småland</option>
                {AREAS.map(a => <option key={a} value={a} data-id="element-885" data-mp="34rktbhy5n1ik">
                    {a}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
      </Reveal>

      <p className="mt-8 flex items-baseline gap-3" data-id="element-886" data-mp="39otapgbhb1ir">
        <span className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-887" data-mp="3eofs81ycb3xd">
          {results.length} ställen
        </span>
        {results.length > 0 && <Note className="!text-lg" data-id="element-888" data-mp="1kbg41kfw91eh">Alla är värda resan.</Note>}
      </p>

      {results.length === 0 ? <div className="mt-8 border-y-2 border-black py-20 text-center" data-id="element-889" data-mp="1h4xr4v1ehb2">
          <p className="display-xl text-3xl uppercase" data-id="element-890" data-mp="1ifxmtld34ww">Inget här. Än.</p>
          <p className="mt-3 text-black/60" data-id="element-891" data-mp="3stzkf7unryxn">Prova en annan kategori eller en annan ort.</p>
        </div> : <div className="mt-8 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-892" data-mp="3orkyufzlul2o">
          {results.map((place, i) => <Reveal key={place.slug} delay={Math.min(i * 0.04, 0.2)} data-id="element-893" data-mp="kf7eyxhcf9yb" data-mpi={i}>
              <PlaceCard place={place} data-id="element-894" data-mp="kdd3e9ryou6w" />
            </Reveal>)}
        </div>}

      <div className="mt-20" data-id="element-895" data-mp="gbrko7sqxlos">
        <Producers data-id="element-896" data-mp="bej2w8bifjo2" />
        <EventsSection data-id="element-897" data-mp="bdztxh3u85m6" />
      </div>
    </div>;
}